<script setup lang="ts">
import { twMerge } from 'tailwind-merge'
import type { DeepPartial } from '#ui/types'
import type { AsideLink } from '#ui-pro/types'
import type { PropType } from 'vue'
import { getULinkProps } from '#ui/utils'

// Extend AsideLink to support nested children
interface AsideLinkWithChildren extends AsideLink {
  children?: AsideLinkWithChildren[]
  iconActive?: boolean
}

defineOptions({ inheritAttrs: false })

const props = defineProps({
  class: {
    default: undefined,
    type: [
      String,
      Object,
      Array,
    ] as PropType<any>,
  },
  level: {
    default: 0,
    type: Number,
  },
  links: {
    default: () => [],
    type: Array as PropType<AsideLinkWithChildren[]>,
  },
  ui: {
    default: () => ({}),
    type: Object as PropType<DeepPartial<typeof config>>,
  },
})

const appConfig = useAppConfig()

// State for tracking expanded/collapsed links
const expandedLinks = ref<Set<number>>(new Set())

// Initialize all links with children as expanded
watchEffect(() => {
  const newExpandedLinks = new Set<number>()

  props.links.forEach((link, index) => {
    if (link.children?.length) {
      newExpandedLinks.add(index)
    }
  })

  expandedLinks.value = newExpandedLinks
})

// Toggle function for expanding/collapsing nested links
function toggleExpanded(index: number): void {
  if (expandedLinks.value.has(index)) {
    expandedLinks.value.delete(index)
  }
  else {
    expandedLinks.value.add(index)
  }
}

// Check if a link should be expanded (has children and is in expanded set, or has active child)
function isExpanded(index: number, link: AsideLinkWithChildren): boolean {
  if (!link.children?.length) {
    return false
  }

  // Auto-expand if any child is active
  const hasActiveChild = link.children.some((child: AsideLinkWithChildren) => child.active)

  if (hasActiveChild && !expandedLinks.value.has(index)) {
    expandedLinks.value.add(index)
  }

  return expandedLinks.value.has(index) || hasActiveChild
}

const config = computed(() => {
  return {
    active: 'text-primary font-semibold',
    base: 'flex items-center group w-full gap-2',
    children: { wrapper: 'space-y-2 border-l border-gray-200 dark:border-gray-800 ml-3 pl-5' },
    externalIcon: {
      base: 'w-3 h-3 absolute top-0.5 -right-3.5 text-gray-400 dark:text-gray-500',
      name: appConfig.ui.icons.external,
    },
    icon: {
      active: 'bg-primary ring-primary text-background',
      base: 'w-4 h-4 flex-shrink-0',
      inactive: 'bg-gray-100/50 dark:bg-gray-800/50 ring-gray-300 dark:ring-gray-700 group-hover:bg-primary group-hover:ring-primary group-hover:text-background',
      wrapper: 'rounded-md p-1 inline-flex ring-inset ring-1',
    },
    inactive: 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium',
    label: 'text-sm/6 relative flex-1',
    toggle: {
      base: 'w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-200',
      expanded: 'rotate-90',
      icon: 'i-heroicons-chevron-right',
    },
  }
})

const { attrs, ui } = useUI('aside.links', toRef(props, 'ui'), config, toRef(props, 'class'), true)
</script>

<template>
  <div
    v-if="links?.length"
    :class="`mb-2 ${level === 0 ? 'lg:mx-0 space-y-8' : 'space-y-2'}`"
    v-bind="level === 0 ? attrs : {}"
  >
    <div
      v-for="(link, index) in links"
      :key="index"
      class="space-y-2"
    >
      <div class="flex items-center gap-2">
        <ULink
          v-if="!link.children?.length"
          v-slot="{ isActive }"
          v-bind="getULinkProps(link)"
          :active-class="ui.active"
          :class="ui.base"
          :inactive-class="ui.inactive"
          @click="link.click"
        >
          <div
            v-if="link.icon"
            :class="[ui.icon.wrapper, (link?.active || link?.iconActive) || isActive ? ui.icon.active : ui.icon.inactive]"
          >
            <UIcon
              :class="twMerge(ui.icon.base, link.iconClass)"
              :name="link.icon"
            />
          </div>

          <span :class="ui.label">
            {{ link.label }}

            <UIcon
              v-if="link.target === '_blank'"
              :class="ui.externalIcon.base"
              :name="ui.externalIcon.name"
            />
          </span>
        </ULink>

        <template v-else>
          <ULink
            v-slot="{ isActive }"
            v-bind="getULinkProps(link)"
            :active-class="ui.active"
            class="flex-1"
            :class="[ui.base]"
            :inactive-class="ui.inactive"
            @click="link.click"
          >
            <div
              v-if="link.icon"
              :class="[ui.icon.wrapper, (link?.active || link?.iconActive) || isActive ? ui.icon.active : ui.icon.inactive]"
            >
              <UIcon
                :class="twMerge(ui.icon.base, link.iconClass)"
                :name="link.icon"
              />
            </div>

            <span :class="ui.label">
              {{ link.label }}

              <UIcon
                v-if="link.target === '_blank'"
                :class="ui.externalIcon.base"
                :name="ui.externalIcon.name"
              />
            </span>
          </ULink>

          <button
            :class="[ui.toggle.base, isExpanded(index, link) ? ui.toggle.expanded : '']"
            @click="toggleExpanded(index)"
          >
            <UIcon :name="ui.toggle.icon" />
          </button>
        </template>
      </div>

      <div
        v-if="link.children?.length && isExpanded(index, link)"
        :class="ui.children.wrapper"
      >
        <AsideLinks
          :level="level + 1"
          :links="link.children"
        />
      </div>
    </div>
  </div>
</template>
